import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Card, Grid } from "@mui/material";
import useThemeRender from "../../theme/useThemeRender";
import { formatDate } from "../../utils/function";
import LazyImage from "../../utils/LazyImage";
import { useSelector } from "react-redux";

function DocumentDetails({ documentData, content, likeCount }) {
  const insideCardBackgroundColorLayer4 = useThemeRender(
    "insideCardBackgroundColorLayer4"
  );
  const textColorLayer4 = useThemeRender("textColorLayer4");
  const textColorLayer1 = useThemeRender("textColorLayer1");
  const insideCardBackgroundColorLayer3 = useThemeRender(
    "insideCardBackgroundColorLayer3"
  );

  const { flagUser } = useSelector((store) => store.user);

  const gridData1 = [
    { label: "Visibility", value: documentData?.visibility, highlighted: true },
    { label: "View count", value: documentData?.viewCount },
    { label: "Likes count", value: likeCount },
    { label: "Content level", value: documentData?.contentLevel },
    {
      label: "Document type",
      value: documentData?.documentType,
      visible: content !== "video",
    },
    { label: "Language", value: documentData?.languages[0] },
    { label: "Version", value: documentData?.version },
    { label: "Edited by", value: documentData?.editedBy },
    { label: "Upload date", value: formatDate(documentData?.createdOn) },
    { label: "Uploaded by", value: documentData?.createdBy },
    { label: "Publication date", value: formatDate(documentData?.reviewDate) },
    { label: "IP Ownership", value: documentData?.ipOwnership },
    { label: "Flesh Color ", value: documentData?.fleshColor },
  ];

  const grid =
    flagUser?.roleName?.toLowerCase() !== "external"
      ? [
          ...gridData1,
          {
            label: "Legal review",
            value: documentData?.isLegalReview ? "Yes" : "No",
          },
        ]
      : gridData1;

  return (
    <Card
      className="SummaryCard"
      sx={{ background: useThemeRender("backgroundColorLayer3") }}
      data-testid="test-doumentdetails"
    >
      <Typography
        className="SummaryTitle"
        sx={{ color: useThemeRender("textColorLayer2") }}
      >
        Details
      </Typography>
      <Box
        sx={{
          display: "flex",
          padding: "var(--Spacing-spacing-16, 16px)",
          flexDirection: "column",
          alignItems: "flex-start",
          alignSelf: "stretch",
          background: useThemeRender("backgroundColorLayer1"),
          height: "auto",
        }}
      >
        <LazyImage width={276} url={documentData?.cdnUrl} />
        <Grid
          container
          rowSpacing={1.5}
          columnSpacing={2}
          className="DetailCardGridContainer"
        >
          {grid.map(
            (item) =>
              item.visible !== false && (
                <Grid
                  item
                  xs={12}
                  sm={3}
                  md={item.label === "Subjects" ? 12 : 3}
                  key={item.label}
                  className="DetailCardGrid"
                >
                  <Box
                    className="DetailCardBox"
                    sx={{
                      background: insideCardBackgroundColorLayer3,
                    }}
                  >
                    <Typography
                      className="DetailCardGridFirstContent"
                      sx={{ color: textColorLayer4 }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      className="DetailCardGridSecondContent"
                      sx={{
                        ...(item.highlighted
                          ? {
                              color: "#005CBC",
                              fontWeight: 700,
                              fontSize:
                                "var(--Font-Size-Small, 14px) !important",
                              background:
                                "var(--Tokens-Button-Group-Hover, #D7E5F8)",
                              padding: "2px 8px",
                              borderRadius:
                                "var(--Corner-radius-effect-corner-1, 4px)",
                              display: "inline-block",
                            }
                          : { color: textColorLayer4 }),
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                </Grid>
              )
          )}
          <Grid item xs={12} className="DetailCardGrid">
            <Box
              sx={{
                display: "flex",
                width: "100%",
                height: "auto",
                padding:
                  "var(--Spacing-spacing-8, 8px) var(--Spacing-spacing-16, 16px)",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "var(--Spacing-spacing-8, 8px)",
                borderRadius: "var(--Corner-radius-corner-radius-small, 8px)",
                background: useThemeRender("insideCardBackgroundColorLayer3"),
              }}
            >
              <Typography
                className="DetailCardGridFirstContent"
                sx={{
                  color: useThemeRender("textColorLayer4"),
                }}
              >
                Subjects
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  flexWrap: "wrap",
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--Spacing-spacing-12, 12px)",
                  height: "auto",
                }}
              >
                {documentData?.subjects?.map((val) => (
                  <Typography
                    sx={{
                      background: insideCardBackgroundColorLayer4,
                      color: textColorLayer1,
                      padding: "4px 4px",
                      lineHeight: "16px",
                      borderRadius: "4px",
                      fontSize: "var(--Font-font-size-small, 12px)",
                    }}
                    key={val}
                  >
                    {val}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} className="DetailCardGrid">
            <DetailCardSection
              title={"Keywords"}
              items={documentData?.keywords?.split(",") || []}
              useThemeRender={useThemeRender}
              textColorLayer1={textColorLayer1}
              insideCardBackgroundColorLayer4={insideCardBackgroundColorLayer4}
            />
          </Grid>

          <Grid item xs={12} className="DetailCardGrid">
            <DetailCardSection
              title="SME"
              items={documentData?.smeContacts || []}
              useThemeRender={useThemeRender}
              textColorLayer1={textColorLayer1}
              insideCardBackgroundColorLayer4={insideCardBackgroundColorLayer4}
            />
          </Grid>
          <Grid item xs={12} className="DetailCardGrid">
            <DetailCardSection
              title="Varieties"
              items={documentData?.varieties || []}
              useThemeRender={useThemeRender}
              textColorLayer1={textColorLayer1}
              insideCardBackgroundColorLayer4={insideCardBackgroundColorLayer4}
            />
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
}

const DetailCardSection = ({
  title,
  items,
  useThemeRender,
  textColorLayer1,

  insideCardBackgroundColorLayer4,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "auto",
        padding:
          "var(--Spacing-spacing-8, 8px) var(--Spacing-spacing-16, 16px)",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "var(--Spacing-spacing-8, 8px)",
        borderRadius: "var(--Corner-radius-corner-radius-small, 8px)",
        background: useThemeRender("insideCardBackgroundColorLayer3"),
      }}
    >
      <Typography
        className="DetailCardGridFirstContent"
        sx={{ color: useThemeRender("textColorLayer4") }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          width: "100%",
          flexWrap: "wrap",
          display: "flex",
          alignItems: "center",
          gap: "var(--Spacing-spacing-12, 12px)",
          height: "auto",
        }}
      >
        {items?.map((val) => (
          <Typography
            sx={{
              background: insideCardBackgroundColorLayer4,
              color: textColorLayer1,
              padding: "4px 4px",
              lineHeight: "16px",
              borderRadius: "4px",
              fontSize: "var(--Font-font-size-small, 12px)",
            }}
            key={val}
          >
            {val}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

// ✅ PropTypes Validation
DocumentDetails.propTypes = {
  documentData: PropTypes.shape({
    viewCount: PropTypes.number,
    likes: PropTypes.number,
    contentLevel: PropTypes.string,
    visibility: PropTypes.string,
    documentType: PropTypes.string,
    languages: PropTypes.arrayOf(PropTypes.string),
    version: PropTypes.string,
    editedBy: PropTypes.string,
    createdOn: PropTypes.string,
    createdBy: PropTypes.string,
    reviewDate: PropTypes.string,
    isLegalReview: PropTypes.bool,
    subjects: PropTypes.arrayOf(PropTypes.string),
    keywords: PropTypes.string,
    thumbnailFileId: PropTypes.number,
  }).isRequired,
  content: PropTypes.oneOf(["video", "document"]), // Ensures only "video" or "document" are valid
};

export default DocumentDetails;
